import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "../../lib/api";

function money(value) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function getImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${url}`;
}

function ProductCard({ product, onAdd }) {
  const disabled = product.sold_out || product.available === false;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onAdd(product)}
      className={[
        "w-full overflow-hidden rounded-2xl border border-border bg-card text-left shadow-sm",
        "transition active:scale-[0.99]",
        disabled
          ? "cursor-not-allowed opacity-55 grayscale"
          : "hover:shadow-md",
      ].join(" ")}
    >
      <div className="aspect-[4/3] w-full bg-secondary">
        {product.image_url ? (
          <img
            src={getImageUrl(product.image_url)}
            alt={product.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-4xl text-muted-foreground">
            🍽️
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="font-semibold text-foreground">{product.name}</div>

        {product.description ? (
          <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
            {product.description}
          </div>
        ) : null}

        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="font-bold text-foreground">
            {money(product.base_price)}
          </span>

          {disabled ? (
            <span className="rounded-full bg-secondary px-2 py-1 text-[10px] font-bold tracking-wide text-muted-foreground">
              SOLD OUT
            </span>
          ) : (
            <span className="rounded-full bg-card px-3 py-1 text-xs font-semibold text-primary-foregrounde">
              + Tambah
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function ProductSheet({ product, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [selectedModifiers, setSelectedModifiers] = useState([]);

  useEffect(() => {
    setQty(1);
    setNotes("");
    setSelectedVariants([]);
    setSelectedModifiers([]);
  }, [product]);

  if (!product) return null;

  const variantGroups = product.variant_groups || [];
  const modifierGroups = product.modifier_groups || [];

  const selectVariant = (group, option) => {
    setSelectedVariants((current) => {
      const withoutGroup = current.filter(
        (id) => !group.options.some((x) => x.id === id)
      );
      return [...withoutGroup, option.id];
    });
  };

  const toggleModifier = (group, option) => {
    setSelectedModifiers((current) => {
      const selected = current.includes(option.id);

      // multi=false: hanya satu pilihan dalam modifier group ini.
      if (!group.multi) {
        if (selected) {
          return current;
        }

        const withoutGroup = current.filter(
          (id) => !(group.options || []).some((x) => x.id === id)
        );

        return [...withoutGroup, option.id];
      }

      // multi=true: boleh pilih lebih dari satu.
      return selected
        ? current.filter((id) => id !== option.id)
        : [...current, option.id];
    });
  };

  const selectedVariantObjects = variantGroups
    .flatMap((g) => g.options || [])
    .filter((o) => selectedVariants.includes(o.id));

  const selectedModifierObjects = modifierGroups
    .flatMap((g) => g.options || [])
    .filter((o) => selectedModifiers.includes(o.id));

  const extra =
    selectedVariantObjects.reduce(
      (sum, option) => sum + Number(option.price_delta || 0),
      0
    ) +
    selectedModifierObjects.reduce(
      (sum, option) => sum + Number(option.price || 0),
      0
    );

  const unitPrice = Number(product.base_price || 0) + extra;

  const requiredVariantMissing = variantGroups
    .filter((g) => g.required)
    .some(
      (g) =>
        !(g.options || []).some((option) =>
          selectedVariants.includes(option.id)
        )
    );

  return (
    <div className="fixed inset-0 z-50 bg-black/40">
      <div className="absolute inset-x-0 bottom-0 max-h-[90vh] overflow-y-auto rounded-t-3xl border border-border bg-card p-5 shadow-2xl">
        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-secondary" />

        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-foreground">
              {product.name}
            </h2>
            <div className="mt-1 font-semibold text-foreground">
              {money(unitPrice)}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-secondary px-3 py-2 text-sm"
          >
            ✕
          </button>
        </div>

        {variantGroups.map((group) => (
          <div key={group.id} className="mt-5">
            <div className="mb-2 font-semibold text-foreground">
              {group.name}
              {group.required ? (
                <span className="ml-1 text-xs text-red-500">Wajib</span>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {(group.options || []).map((option) => {
                const selected = selectedVariants.includes(option.id);

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => selectVariant(group, option)}
                    className={[
                      "rounded-xl border p-3 text-left text-sm",
                      selected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card",
                    ].join(" ")}
                  >
                    <div className="font-medium">{option.name}</div>
                    {option.price_delta ? (
                      <div className="mt-1 text-xs opacity-70">
                        +{money(option.price_delta)}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {modifierGroups.map((group) => (
          <div key={group.id} className="mt-5">
            <div className="mb-2 font-semibold text-foreground">
              {group.name}
            </div>

            <div className="space-y-2">
              {(group.options || []).map((option) => {
                const selected = selectedModifiers.includes(option.id);

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => toggleModifier(group, option)}
                    className={[
                      "flex w-full items-center justify-between rounded-xl border p-3 text-left",
                      selected
                        ? "border-primary bg-primary/10"
                        : "border-border",
                    ].join(" ")}
                  >
                    <span>{option.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {option.price ? `+${money(option.price)}` : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-5">
          <label className="mb-2 block font-semibold text-foreground">
            Catatan
          </label>

          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 500))}
            placeholder="Contoh: tidak terlalu manis"
            className="min-h-24 w-full rounded-xl border border-border bg-background text-foreground p-3 text-sm outline-none focus:border-primary"
          />
        </div>

        <div className="mt-5 flex items-center gap-3">
          <div className="flex items-center rounded-xl border border-border bg-background">
            <button
              type="button"
              onClick={() => setQty((x) => Math.max(1, x - 1))}
              className="px-4 py-3"
            >
              −
            </button>
            <span className="min-w-10 text-center font-semibold">{qty}</span>
            <button
              type="button"
              onClick={() => setQty((x) => x + 1)}
              className="px-4 py-3"
            >
              +
            </button>
          </div>

          <button
            type="button"
            disabled={requiredVariantMissing}
            onClick={() =>
              onAdd({
                product,
                qty,
                notes,
                variant_option_ids: selectedVariants,
                modifier_option_ids: selectedModifiers,
              })
            }
            className="flex-1 rounded-xl bg-primary px-4 py-3 font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            {requiredVariantMissing
              ? "Pilih varian wajib"
              : `Tambah • ${money(unitPrice * qty)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SelfOrderPage() {
  const { pathname } = window.location;
  const token = pathname.split("/self-order/")[1]?.split("/")[0] || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [menu, setMenu] = useState(null);
  const [payment, setPayment] = useState(null);
  const [activeProduct, setActiveProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [view, setView] = useState("MENU");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [checkoutError, setCheckoutError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("QR meja tidak valid.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      try {
        setLoading(true);

        const [resolver, menuResponse, paymentResponse] = await Promise.all([
          api.get(`/public/self-order/table/${token}`),
          api.get(`/public/self-order/table/${token}/menu`),
          api.get(`/public/self-order/table/${token}/payment`),
        ]);

        if (cancelled) return;

        setMenu({
          ...menuResponse.data,
          table: resolver.data.table,
          outlet: resolver.data.outlet,
          self_order: resolver.data.self_order,
        });

        setPayment(paymentResponse.data.payment);
      } catch (e) {
        if (!cancelled) setError(apiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const orderId = order?.order_id || order?.id;

    if (view !== "WAITING" || !orderId || !token) {
      return undefined;
    }

    let cancelled = false;

    async function checkOrderStatus() {
      try {
        const response = await api.get(
          `/public/self-order/table/${token}/orders/${orderId}/status`
        );

        if (cancelled) return;

        const data = response.data;

        setOrder((current) => ({
          ...current,
          ...data,
        }));

        if (data.status === "PAID") {
          setView("RECEIPT");
        } else if (data.status === "CANCELLED") {
          setView("CANCELLED");
        }
      } catch (e) {
        // Jangan mengganggu layar customer jika satu kali polling gagal.
      }
    }

    checkOrderStatus();

    const intervalId = window.setInterval(checkOrderStatus, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [view, order?.order_id, order?.id, token]);

  const products = useMemo(() => {
    if (!menu) return [];

    const categorized = (menu.categories || []).flatMap(
      (category) =>
        (category.products || []).map((product) => ({
          ...product,
          category_name: category.name,
        }))
    );

    return [...categorized, ...(menu.uncategorized_products || [])];
  }, [menu]);

  const categories = useMemo(() => {
    const names = products
      .map((x) => x.category_name)
      .filter(Boolean);

    return ["Semua", ...Array.from(new Set(names))];
  }, [products]);

  const [category, setCategory] = useState("Semua");

  const visibleProducts =
    category === "Semua"
      ? products
      : products.filter((x) => x.category_name === category);

  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  const cartTotal = cart.reduce((sum, item) => {
    const variantExtra = (item.variant_option_ids || []).reduce((s, id) => {
      const option = (item.product.variant_groups || [])
        .flatMap((g) => g.options || [])
        .find((x) => x.id === id);

      return s + Number(option?.price_delta || 0);
    }, 0);

    const modifierExtra = (item.modifier_option_ids || []).reduce((s, id) => {
      const option = (item.product.modifier_groups || [])
        .flatMap((g) => g.options || [])
        .find((x) => x.id === id);

      return s + Number(option?.price || 0);
    }, 0);

    return (
      sum +
      (Number(item.product.base_price || 0) +
        variantExtra +
        modifierExtra) *
        item.qty
    );
  }, 0);

  function addToCart(item) {
    setCart((current) => [
      ...current,
      {
        ...item,
        cart_id: `${item.product.id}-${Date.now()}-${Math.random()}`,
      },
    ]);
    setActiveProduct(null);
  }

  function removeCartItem(cartId) {
    setCart((current) => current.filter((x) => x.cart_id !== cartId));
  }

  async function checkout() {
    if (!customerName.trim()) {
      setCheckoutError("Nama pelanggan wajib diisi.");
      return;
    }

    if (!cart.length) {
      setCheckoutError("Keranjang masih kosong.");
      return;
    }

    setCheckoutError("");

    try {
      setCheckoutLoading(true);

      const clientTransactionId = `SO-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;

      const response = await api.post(
        `/public/self-order/table/${token}/checkout`,
        {
          client_transaction_id: clientTransactionId,
          customer_name: customerName.trim(),
          items: cart.map((item) => ({
            product_id: item.product.id,
            order_type: "DINE IN",
            variant_option_ids: item.variant_option_ids || [],
            modifier_option_ids: item.modifier_option_ids || [],
            qty: item.qty,
            notes: item.notes || "",
          })),
        }
      );

      setOrder(response.data);
      setView("PAYMENT");
    } catch (e) {
      setCheckoutError(apiError(e));
    } finally {
      setCheckoutLoading(false);
    }
  }

  async function submitPayment() {
    if (!order?.order_id && !order?.id) return;

    const orderId = order.order_id || order.id;

    try {
      setPaymentSubmitting(true);
      setCheckoutError("");

      const response = await api.post(
        `/public/self-order/table/${token}/orders/${orderId}/payment-submitted`
      );

      setOrder((current) => ({
        ...current,
        ...response.data,
        status: "PAYMENT_SUBMITTED",
      }));

      setView("WAITING");
    } catch (e) {
      setCheckoutError(apiError(e));
    } finally {
      setPaymentSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Menyiapkan menu…</div>
      </div>
    );
  }

  if (error || !menu) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
          <div className="text-xl font-bold text-foreground">
            Self Order tidak tersedia
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            {error || "Silakan scan ulang QR meja."}
          </div>
        </div>
      </div>
    );
  }

  if (view === "PAYMENT" || view === "WAITING") {
    const grandTotal = Number(
      order?.grand_total || order?.total || cartTotal || 0
    );

    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto min-h-screen max-w-md bg-card">
          <div className="border-b border-border p-5">
            <div className="text-xs font-medium text-muted-foreground">
              {menu.outlet?.name}
            </div>
            <div className="mt-1 text-xl font-bold text-foreground">
              Meja {menu.table?.name || "-"}
            </div>
            {menu.table?.code ? (
              <div className="mt-1 text-xs font-medium text-muted-foreground">
                {menu.table.code}
              </div>
            ) : null}
          </div>

          <div className="p-5">
            <div className="rounded-2xl bg-background p-5 text-center">
              <div className="text-sm text-muted-foreground">Total pesanan</div>
              <div className="mt-1 text-3xl font-black text-foreground">
                {money(grandTotal)}
              </div>
            </div>

            {view === "WAITING" ? (
                <div className="mt-6 space-y-4">
                  <div className="rounded-2xl border border-border bg-card p-5 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-2xl">
                      ✓
                    </div>

                    <div className="mt-4 text-xl font-bold text-foreground">
                      Pesanan berhasil dikirim
                    </div>

                    <div className="mt-2 text-sm leading-6 text-muted-foreground">
                      Terima kasih, pesanan kamu sudah dikirim ke kasir.
                    </div>
                  </div>

                  <div className="rounded-2xl border border-border bg-card p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xs font-medium text-muted-foreground">
                          Status pesanan
                        </div>
                        <div className="mt-1 font-bold text-foreground">
                          Menunggu konfirmasi kasir
                        </div>
                      </div>

                      <span className="rounded-full bg-secondary px-3 py-1 text-[11px] font-bold text-muted-foreground">
                        DIPROSES
                      </span>
                    </div>

                    <div className="mt-4 space-y-3 border-t border-border pt-4">
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-sm text-muted-foreground">
                          No. Pesanan
                        </span>
                        <span className="text-right text-sm font-semibold text-foreground">
                          {order?.transaction_number || order?.order_id || order?.id || "-"}
                        </span>
                      </div>

                      <div className="flex items-start justify-between gap-4">
                        <span className="text-sm text-muted-foreground">
                          Meja
                        </span>
                        <span className="text-right text-sm font-semibold text-foreground">
                          {menu.table?.name || "-"}
                          {menu.table?.code ? (
                            <span className="block text-xs font-medium text-muted-foreground">
                              {menu.table.code}
                            </span>
                          ) : null}
                        </span>
                      </div>

                      <div className="flex items-start justify-between gap-4">
                        <span className="text-sm text-muted-foreground">
                          Nama
                        </span>
                        <span className="text-right text-sm font-semibold text-foreground">
                          {order?.customer_name || customerName || "-"}
                        </span>
                      </div>

                      <div className="flex items-start justify-between gap-4">
                        <span className="text-sm text-muted-foreground">
                          Total
                        </span>
                        <span className="text-right text-sm font-bold text-foreground">
                          {money(grandTotal)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-background p-4 text-center">
                    <div className="text-sm font-semibold text-foreground">
                      Pembayaran sedang diperiksa kasir
                    </div>
                    <div className="mt-1 text-sm leading-6 text-muted-foreground">
                      Setelah pembayaran dikonfirmasi, pesanan kamu akan segera diproses.
                    </div>
                  </div>
                </div>
              ) : (
              <>
                <div className="mt-6">
                  <div className="text-lg font-bold text-foreground">
                    Pembayaran QRIS
                  </div>

                  <div className="mt-2 text-sm leading-6 text-muted-foreground">
                    Pastikan anda sudah melakukan pembayaran sesuai jumlah
                    pesanan.
                  </div>
                </div>

                {payment?.enabled ? (
                  <div className="mt-5 rounded-2xl border border-border bg-card p-4">
                    <img
                      src={getImageUrl(payment.image_url)}
                      alt="QRIS pembayaran"
                      className="mx-auto aspect-square w-full max-w-xs object-contain"
                    />

                    <div className="mt-4 text-center">
                      <div className="font-semibold text-foreground">
                        {payment.merchant_name}
                      </div>
                      {payment.merchant_city ? (
                        <div className="text-sm text-muted-foreground">
                          {payment.merchant_city}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="mt-5 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">
                    QRIS pembayaran belum tersedia. Silakan hubungi kasir.
                  </div>
                )}

                {checkoutError ? (
                  <div className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">
                    {checkoutError}
                  </div>
                ) : null}

                <button
                  type="button"
                  disabled={paymentSubmitting || !payment?.enabled}
                  onClick={submitPayment}
                  className="mt-5 w-full rounded-xl bg-primary px-4 py-4 font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {paymentSubmitting
                    ? "Mengirim…"
                    : "Saya Sudah Bayar"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === "CANCELLED") {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto flex min-h-screen max-w-md flex-col bg-card p-5">
          <div className="flex flex-1 flex-col justify-center">
            <div className="rounded-2xl border border-red-500/30 bg-card p-6 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
                <span className="text-3xl text-red-500">×</span>
              </div>

              <h1 className="mt-5 text-2xl font-black text-foreground">
                Pesanan Dibatalkan
              </h1>

              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Pesanan ini telah dibatalkan oleh kasir.
              </p>

              <div className="mt-6 rounded-2xl border border-border bg-background p-4 text-left">
                <div className="flex items-start justify-between gap-4">
                  <span className="text-sm text-muted-foreground">
                    No. Pesanan
                  </span>
                  <span className="text-right text-sm font-bold text-foreground">
                    {order?.transaction_number || "-"}
                  </span>
                </div>

                <div className="mt-4 flex items-start justify-between gap-4">
                  <span className="text-sm text-muted-foreground">
                    Meja
                  </span>
                  <span className="text-right text-sm font-semibold text-foreground">
                    {menu.table?.name || "-"}
                    {menu.table?.code ? (
                      <span className="block text-xs font-medium text-muted-foreground">
                        {menu.table.code}
                      </span>
                    ) : null}
                  </span>
                </div>

                <div className="mt-4 flex items-start justify-between gap-4">
                  <span className="text-sm text-muted-foreground">
                    Nama
                  </span>
                  <span className="text-right text-sm font-semibold text-foreground">
                    {order?.customer_name || customerName || "-"}
                  </span>
                </div>

                <div className="mt-4 flex items-start justify-between gap-4">
                  <span className="text-sm text-muted-foreground">
                    Status
                  </span>
                  <span className="rounded-full bg-red-500/10 px-3 py-1 text-xs font-bold text-red-500">
                    DIBATALKAN
                  </span>
                </div>
              </div>

              <div className="mt-4 rounded-2xl bg-background p-4 text-sm leading-6 text-muted-foreground">
                Pesanan ini tidak diproses sebagai pembayaran.
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => {
                setOrder(null);
                setCart([]);
                setActiveProduct(null);
                setCustomerName("");
                setCheckoutError("");
                setView("MENU");
              }}
              className="w-full rounded-xl bg-primary px-4 py-4 font-bold text-primary-foreground"
            >
              KEMBALI KE MENU
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === "RECEIPT") {
    const receipt = order?.receipt || {};
    const receiptItems = receipt.items || [];
    const receiptPayment = receipt.payment || {};

    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto min-h-screen max-w-md bg-card p-5">
          <div className="flex min-h-screen flex-col">
            <div className="flex-1">
              <div className="pt-8 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <span className="text-3xl text-primary">✓</span>
                </div>

                <h1 className="mt-5 text-2xl font-black text-foreground">
                  Pembayaran Berhasil
                </h1>

                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Pembayaran kamu sudah diterima.
                </p>

                <div className="mx-auto mt-5 max-w-sm rounded-2xl border-2 border-primary/30 bg-primary/10 p-4 text-left">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-lg text-primary-foreground">
                      📸
                    </div>
                    <div>
                      <div className="font-bold text-foreground">
                        Simpan Bukti Pesanan
                      </div>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">
                        Screenshot halaman ini untuk menyimpan bukti pembayaran dan pesanan kamu.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 inline-flex rounded-full bg-primary/10 px-4 py-2 text-sm font-bold text-primary">
                  LUNAS
                </div>
              </div>

              <div className="mt-8 rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-4">
                  <span className="text-sm text-muted-foreground">
                    No. Pesanan
                  </span>
                  <span className="text-right text-sm font-bold text-foreground">
                    {order?.transaction_number || "-"}
                  </span>
                </div>

                <div className="mt-4 flex items-start justify-between gap-4">
                  <span className="text-sm text-muted-foreground">
                    Nama
                  </span>
                  <span className="text-right text-sm font-semibold text-foreground">
                    {order?.customer_name || customerName || "-"}
                  </span>
                </div>

                <div className="mt-4 flex items-start justify-between gap-4">
                  <span className="text-sm text-muted-foreground">
                    Meja
                  </span>
                  <span className="text-right text-sm font-semibold text-foreground">
                    {menu.table?.name || "-"}
                    {menu.table?.code ? (
                      <span className="block text-xs font-medium text-muted-foreground">
                        {menu.table.code}
                      </span>
                    ) : null}
                  </span>
                </div>

                <div className="mt-4 flex items-start justify-between gap-4">
                  <span className="text-sm text-muted-foreground">
                    Pembayaran
                  </span>
                  <span className="text-right text-sm font-semibold text-foreground">
                    {receiptPayment.method_name ||
                      receiptPayment.method_type ||
                      "QRIS"}
                  </span>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-border bg-card p-4">
                <div className="text-sm font-bold text-foreground">
                  Detail Pesanan
                </div>

                <div className="mt-4 space-y-4">
                  {receiptItems.map((item, index) => (
                    <div
                      key={item.id || item.product_id || index}
                      className="flex items-start justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground">
                          {item.product_name || item.name || "Menu"}
                        </div>

                        <div className="mt-1 text-xs text-muted-foreground">
                          {item.qty || 0} × {money(item.unit_price || 0)}
                        </div>

                        {item.variant_name ? (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {item.variant_name}
                          </div>
                        ) : null}
                      </div>

                      <div className="shrink-0 text-sm font-bold text-foreground">
                        {money(item.line_total || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-border bg-card p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold text-foreground">
                    {money(receipt.subtotal || 0)}
                  </span>
                </div>

                {receipt.tax?.amount ? (
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {receipt.tax.name || "Pajak"}
                    </span>
                    <span className="font-semibold text-foreground">
                      {money(receipt.tax.amount)}
                    </span>
                  </div>
                ) : null}

                {receipt.service_charge?.amount ? (
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {receipt.service_charge.name || "Service"}
                    </span>
                    <span className="font-semibold text-foreground">
                      {money(receipt.service_charge.amount)}
                    </span>
                  </div>
                ) : null}

                <div className="mt-4 border-t border-border pt-4">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-foreground">TOTAL</span>
                    <span className="text-lg font-black text-foreground">
                      {money(receipt.grand_total || order?.grand_total || 0)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-2xl bg-background p-4 text-center text-sm leading-6 text-muted-foreground">
                Terima kasih. Pesanan kamu sudah dibayar dan diterima.
              </div>
            </div>

            <div className="sticky bottom-0 mt-6 bg-card pt-4">
              <button
                type="button"
                                onClick={() => {
                  setOrder(null);
                  setCart([]);
                  setActiveProduct(null);
                  setCustomerName("");
                  setCheckoutError("");
                  setView("MENU");
                }}
                className="w-full rounded-xl bg-primary px-4 py-4 font-bold text-primary-foreground"
              >
                KEMBALI KE MENU
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto min-h-screen max-w-md bg-card pb-28">
        <header className="border-b border-border p-5">
          <div className="text-xs font-medium text-muted-foreground">
            {menu.outlet?.name}
          </div>

          <div className="mt-1 flex items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-black text-foreground">
                {menu.tenant?.name || "Menu"}
              </h1>

              <div className="mt-1 text-sm font-semibold text-foreground">
                Meja {menu.table?.name || "-"}
              </div>
              {menu.table?.code ? (
                <div className="mt-1 text-xs font-medium text-muted-foreground">
                  {menu.table.code}
                </div>
              ) : null}
            </div>

            <div className="rounded-full bg-secondary px-3 py-2 text-xs font-semibold text-foreground">
              Self Order
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-background p-4 text-sm leading-6 text-muted-foreground">
            <strong className="text-foreground">Cara pesan:</strong>
            <br />
            Pilih makanan dan minuman yang kamu inginkan. Pilih varian dan isi
            catatan untuk menu bila diperlukan.
          </div>

          <div className="mt-4">
            <label className="mb-2 block text-sm font-semibold text-foreground">
              Nama Anda
            </label>
            <input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value.slice(0, 150))}
              placeholder="Masukkan nama"
              className="w-full rounded-xl border border-border bg-background px-4 py-3 outline-none focus:border-primary"
            />
          </div>
        </header>

        <div className="sticky top-0 z-20 overflow-x-auto border-b border-border bg-card px-4 py-3">
          <div className="flex gap-2">
            {categories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                className={[
                  "whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold",
                  category === item
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-muted-foreground",
                ].join(" ")}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <main className="grid grid-cols-2 gap-3 p-4">
          {visibleProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onAdd={setActiveProduct}
            />
          ))}
        </main>

        {cart.length > 0 ? (
          <div className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-md border-t border-border bg-card p-3 shadow-2xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs text-muted-foreground">
                  {cartCount} item
                </div>
                <div className="font-black text-foreground">
                  {money(cartTotal)}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setView("CART")}
                className="rounded-xl bg-primary px-5 py-3 font-bold text-primary-foreground"
              >
                Lihat Keranjang
              </button>
            </div>
          </div>
        ) : null}

        {activeProduct ? (
          <ProductSheet
            product={activeProduct}
            onClose={() => setActiveProduct(null)}
            onAdd={addToCart}
          />
        ) : null}

        {view === "CART" ? (
          <div className="fixed inset-0 z-50 bg-black/40">
            <div className="absolute inset-x-0 bottom-0 max-h-[90vh] overflow-y-auto rounded-t-3xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-black">Keranjang</h2>
                <button
                  type="button"
                  onClick={() => setView("MENU")}
                  className="rounded-full bg-secondary px-3 py-2"
                >
                  ✕
                </button>
              </div>

              <div className="mt-5 space-y-3">
                {cart.map((item) => (
                  <div
                    key={item.cart_id}
                    className="rounded-2xl border border-border bg-background p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-bold">{item.product.name}</div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          Qty {item.qty}
                        </div>
                        {item.notes ? (
                          <div className="mt-1 text-xs text-muted-foreground">
                            Catatan: {item.notes}
                          </div>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeCartItem(item.cart_id)}
                        className="text-xs font-semibold text-red-600"
                      >
                        Hapus
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {checkoutError ? (
                <div className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">
                  {checkoutError}
                </div>
              ) : null}

              <div className="mt-5 flex items-center justify-between">
                <span className="font-semibold text-muted-foreground">Total</span>
                <span className="text-xl font-black">
                  {money(cartTotal)}
                </span>
              </div>

              <button
                type="button"
                onClick={checkout}
                disabled={checkoutLoading}
                className="mt-4 w-full rounded-xl bg-primary px-4 py-4 font-bold text-primary-foreground disabled:opacity-40"
              >
                {checkoutLoading ? "Memproses…" : "Lanjut ke Pembayaran"}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
